import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, TrendingUp, Wallet, Users, CalendarDays, Download, Loader2, Filter } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";

type Tab = "finance_pnl" | "finance_aging" | "finance_methods" | "academic" | "crm" | "hr";

function money(minor: number) { return (minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function ReportsCenter() {
    const { t, i18n } = useTranslation();
    const { hasPerm, hasModuleAccess, me } = useAuth();
    const isPO = !!me?.user.is_platform_owner;
    const canView = hasPerm("reports.view");
    const canExport = hasPerm("reports.export");
    const [tab, setTab] = useState<Tab>("finance_pnl");
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [months, setMonths] = useState(6);

    // A tab shows only if the tenant has its module enabled and the user has the matching permission.
    const tabs = useMemo(() => ([
        { key: "finance_pnl", icon: TrendingUp, labelKey: "reports.tab.finance_pnl", module: "finance", perm: "finance.view" },
        { key: "finance_aging", icon: Wallet, labelKey: "reports.tab.finance_aging", module: "finance", perm: "finance.view" },
        { key: "finance_methods", icon: Wallet, labelKey: "reports.tab.finance_methods", module: "finance", perm: "finance.view" },
        { key: "academic", icon: CalendarDays, labelKey: "reports.tab.academic", module: "attendance" },
        { key: "crm", icon: Users, labelKey: "reports.tab.crm", module: "crm", perm: "crm.view" },
        { key: "hr", icon: Users, labelKey: "reports.tab.hr", module: "hr.payroll", perm: "payroll.view" },
    ] as { key: Tab; icon: any; labelKey: string; module: string; perm?: string }[])
        .filter((tt) => isPO || (hasModuleAccess(tt.module, "view") && (!tt.perm || hasPerm(tt.perm)))),
    [isPO, hasModuleAccess, hasPerm]);
    const tabVisible = tabs.some((tt) => tt.key === tab);

    useEffect(() => {
        if (!tabVisible && tabs.length) setTab(tabs[0].key);
    }, [tabVisible, tabs]);

    const endpoint = useCallback((t: Tab) => {
        switch (t) {
            case "finance_pnl": return `/reports/finance/monthly-pnl?months_back=${months}`;
            case "finance_aging": return `/reports/finance/aging`;
            case "finance_methods": return `/reports/finance/payment-methods?months_back=${months}`;
            case "academic": return `/reports/academic/attendance`;
            case "crm": return `/reports/crm/funnel`;
            case "hr": return `/reports/hr/payroll-summary`;
        }
    }, [months]);

    const load = useCallback(async () => {
        setLoading(true); setErr(null); setData(null);
        try {
            const r = await api.get(endpoint(tab));
            setData(r.data);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, [tab, endpoint]);

    useEffect(() => { if (canView && tabVisible) load(); }, [load, canView, tabVisible]);

    const exportCsv = async () => {
        try {
            const r = await api.get(endpoint(tab) + (endpoint(tab).includes("?") ? "&" : "?") + "export=true", { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${tab}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch (e: any) { setErr(formatApiError(e)); }
    };

    if (!canView) {
        return (
            <div className="surface-card p-8 text-center text-sm rounded-xl border" data-testid="reports-noperm" style={{ background: "var(--card)", borderColor: "var(--border)", color: "var(--text-muted)" }}>
                {t("common.no_permission")}
            </div>
        );
    }

    return (
        <ModuleGate module="reports">
            <div className="space-y-4" data-testid="reports-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <BarChart3 size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("reports.title")}</h1>
                    {tabVisible && (tab === "finance_pnl" || tab === "finance_methods") && (
                        <label className="inline-flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                            <Filter size={12} />{t("reports.months_back")}:
                            <select data-testid="reports-months" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10))}
                                className="rounded-md px-2 py-1 text-xs" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }}>
                                {[3, 6, 12, 18, 24].map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </label>
                    )}
                    {canExport && data && (
                        <button data-testid="reports-export" onClick={exportCsv}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm"
                            style={{ background: "var(--input)", color: "var(--text)", minHeight: 40, border: "1px solid var(--border)" }}>
                            <Download size={14} /> {t("reports.export_csv")}
                        </button>
                    )}
                </header>

                <div className="flex flex-wrap gap-2" role="tablist" data-testid="reports-tabs">
                    {tabs.map((tt) => {
                        const Icon = tt.icon;
                        return (
                            <button key={tt.key} role="tab" data-testid={`reports-tab-${tt.key}`} onClick={() => setTab(tt.key)}
                                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs"
                                style={{ background: tab === tt.key ? "var(--primary)" : "var(--input)", color: tab === tt.key ? "var(--primary-fg)" : "var(--text)", minHeight: 40 }}>
                                <Icon size={14} /> {t(tt.labelKey)}
                            </button>
                        );
                    })}
                </div>

                {tabs.length === 0 && (
                    <div className="rounded-xl border p-8 text-center text-sm" data-testid="reports-no-tabs" style={{ background: "var(--card)", borderColor: "var(--border)", color: "var(--text-muted)" }}>
                        {t("reports.no_modules")}
                    </div>
                )}

                {loading && <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>}
                {err && <div data-testid="reports-err" className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

                {data && (
                    <div className="rounded-xl border overflow-hidden" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="reports-content">
                        {data.formula && <div className="px-4 py-2 text-xs border-b" style={{ color: "var(--text-muted)", background: "var(--input)", borderColor: "var(--border)" }} data-testid="reports-formula">📐 {data.formula}</div>}

                        {tab === "finance_pnl" && (
                            <div className="overflow-x-auto"><table className="w-full text-sm">
                                <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("reports.pnl.period")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.pnl.payments")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.pnl.other_income")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.pnl.income")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.pnl.expense")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.pnl.net")}</th>
                                </tr></thead>
                                <tbody>
                                    {data.rows.map((r: any) => (
                                        <tr key={r.period} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`pnl-row-${r.period}`}>
                                            <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{r.period}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{money(r.payments_minor ?? r.income_minor)}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }} data-testid={`pnl-other-${r.period}`}>{money(r.other_income_minor ?? 0)}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--success-fg)" }}>{money(r.income_minor)}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--error-fg)" }}>{money(r.expense_minor)}</td>
                                            <td className="px-3 py-2 text-right font-mono font-semibold" style={{ color: r.net_minor >= 0 ? "var(--success-fg)" : "var(--error-fg)" }}>{money(r.net_minor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}

                        {tab === "finance_aging" && (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
                                    {["current", "1_30", "31_60", "61_90", "over_90"].map((k) => (
                                        <div key={k} className="rounded-md p-3 border" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid={`aging-bucket-${k}`}>
                                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t(`reports.aging.${k}`)}</div>
                                            <div className="text-lg font-mono font-semibold" style={{ color: "var(--text)" }}>{money(data.buckets?.[k] || 0)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-4 py-2 text-xs border-t" style={{ color: "var(--text-muted)", borderColor: "var(--border)" }}>
                                    {t("reports.aging.total")}: <span className="font-mono font-semibold" style={{ color: "var(--text)" }} data-testid="aging-total">{money(data.total_outstanding_minor)}</span>
                                </div>
                                {data.rows?.length > 0 && (
                                    <div className="overflow-x-auto border-t" style={{ borderColor: "var(--border)" }}><table className="w-full text-sm">
                                        <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                            <th className="text-left px-3 py-2">{t("reports.aging.invoice")}</th>
                                            <th className="text-left px-3 py-2">{t("reports.aging.due_date")}</th>
                                            <th className="text-right px-3 py-2">{t("reports.aging.overdue_days")}</th>
                                            <th className="text-right px-3 py-2">{t("reports.aging.outstanding")}</th>
                                            <th className="text-left px-3 py-2">{t("reports.aging.bucket")}</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.rows.map((r: any) => (
                                                <tr key={r.invoice_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`aging-row-${r.invoice_id}`}>
                                                    <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{r.code}</td>
                                                    <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{r.due_date || "—"}</td>
                                                    <td className="px-3 py-2 text-right" style={{ color: "var(--text)" }}>{r.overdue_days}</td>
                                                    <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{money(r.outstanding_minor)}</td>
                                                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{t(`reports.aging.${r.bucket}`)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table></div>
                                )}
                            </>
                        )}

                        {tab === "finance_methods" && (
                            <div className="overflow-x-auto"><table className="w-full text-sm">
                                <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("reports.methods.method")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.methods.count")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.methods.total")}</th>
                                </tr></thead>
                                <tbody>
                                    {data.rows.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("reports.no_data")}</td></tr>}
                                    {data.rows.map((r: any) => (
                                        <tr key={r.method} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`methods-row-${r.method}`}>
                                            <td className="px-3 py-2" style={{ color: "var(--text)" }}>{t(`finance.method.${r.method}`, { defaultValue: r.method })}</td>
                                            <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{r.count}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{money(r.total_minor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}

                        {tab === "academic" && (
                            <div className="overflow-x-auto"><table className="w-full text-sm">
                                <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("reports.academic.group")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.academic.held")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.academic.cancelled")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.academic.marks")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.academic.rate")}</th>
                                </tr></thead>
                                <tbody>
                                    {data.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("reports.no_data")}</td></tr>}
                                    {data.rows.map((r: any) => (
                                        <tr key={r.group_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`acad-row-${r.group_id}`}>
                                            <td className="px-3 py-2" style={{ color: "var(--text)" }}>{r.group_name}</td>
                                            <td className="px-3 py-2 text-right" style={{ color: "var(--text)" }}>{r.sessions_held}</td>
                                            <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{r.sessions_cancelled}</td>
                                            <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{r.present_marks}/{r.attendance_marks}</td>
                                            <td className="px-3 py-2 text-right font-mono font-semibold" style={{ color: r.attendance_rate_pct >= 80 ? "var(--success-fg)" : r.attendance_rate_pct >= 60 ? "var(--warning-fg)" : "var(--error-fg)" }}>{r.attendance_rate_pct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}

                        {tab === "crm" && (
                            <>
                                <div className="grid grid-cols-3 gap-3 p-4">
                                    <div className="rounded-md p-3 border" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="crm-total-in">
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("reports.crm.in_pipeline")}</div>
                                        <div className="text-lg font-mono font-semibold" style={{ color: "var(--text)" }}>{data.total_in_pipeline}</div>
                                    </div>
                                    <div className="rounded-md p-3 border" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="crm-total-won">
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("reports.crm.won")}</div>
                                        <div className="text-lg font-mono font-semibold" style={{ color: "var(--success-fg)" }}>{data.total_won}</div>
                                    </div>
                                    <div className="rounded-md p-3 border" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="crm-total-lost">
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("reports.crm.lost")}</div>
                                        <div className="text-lg font-mono font-semibold" style={{ color: "var(--error-fg)" }}>{data.total_lost}</div>
                                    </div>
                                </div>
                                <div className="overflow-x-auto border-t" style={{ borderColor: "var(--border)" }}><table className="w-full text-sm">
                                    <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                        <th className="text-left px-3 py-2">{t("reports.crm.stage")}</th>
                                        <th className="text-left px-3 py-2">{t("reports.crm.category")}</th>
                                        <th className="text-right px-3 py-2">{t("reports.crm.count")}</th>
                                        <th className="text-right px-3 py-2">{t("reports.crm.conv_pct")}</th>
                                    </tr></thead>
                                    <tbody>
                                        {data.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("reports.no_data")}</td></tr>}
                                        {data.rows.map((r: any) => (
                                            <tr key={r.stage_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`crm-row-${r.code}`}>
                                                <td className="px-3 py-2" style={{ color: "var(--text)" }}>{t(`crm.stages.${r.code}`, { defaultValue: r.name })}</td>
                                                <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{t(`reports.crm.cat.${r.category}`)}</td>
                                                <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{r.count}</td>
                                                <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text-muted)" }}>{r.conv_from_prev_pct != null ? `${r.conv_from_prev_pct}%` : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table></div>
                            </>
                        )}

                        {tab === "hr" && (
                            <div className="overflow-x-auto"><table className="w-full text-sm">
                                <thead><tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("reports.hr.period")}</th>
                                    <th className="text-left px-3 py-2">{t("common.status")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.hr.items")}</th>
                                    <th className="text-right px-3 py-2">{t("reports.hr.total")}</th>
                                </tr></thead>
                                <tbody>
                                    {data.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("reports.no_data")}</td></tr>}
                                    {data.rows.map((r: any) => (
                                        <tr key={r.run_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`hr-row-${r.run_id}`}>
                                            <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{r.period}</td>
                                            <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{t(`payroll.status.${r.status}`, { defaultValue: r.status })}</td>
                                            <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{r.items_count}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{money(r.total_minor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table></div>
                        )}
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
